import {
    formatDateLong,
    formatDateTime,
    formatTime,
    getBrowserTimezone,
} from '../../../../lib/DateTime.js';
import CalendarMarkIcon from '../../../icons/CalendarMarkIcon.jsx';
import CircleClockIcon from '../../../icons/CircleClockIcon.jsx';
import PopoverButton from '../../../PopoverButton.jsx';
import {
    ChevronDownIcon,
    MapPinIcon,
    UserIcon,
} from '@heroicons/react/24/outline/index.js';
import { google, outlook, office365, yahoo, ics } from 'calendar-link';
import AppleIcon from '../../../icons/AppleIcon.jsx';
import GoogleIcon from '../../../icons/GoogleIcon.jsx';
import OutlookIcon from '../../../icons/OutlookIcon.jsx';
import YahooIcon from '../../../icons/YahooIcon.jsx';
import IcsIcon from '../../../icons/IcsIcon.jsx';
import usePublicPostApi from '../../../../public/lib/usePublicPostApi.js';
import AddToCalendar from '../../../../public/components/AddToCalendar.jsx';
import RescheduleButton from '../../../../public/components/RescheduleButton.jsx';
import { useContext } from 'react';
import { ConfirmationContext } from '../../../../lib/ConfirmationContext.js';
import { subDays } from 'date-fns';
import Linkify from '~/components/Linkify.jsx';
import {
    clientFormatDateLong,
    clientFormatDateTimeLong,
    clientFormatTime,
} from '~/public/lib/ClientDateTime.js';
import useClientTimezone from '~/public/lib/useClientTimezone.js';

const EventDetailsRow = ({ icon, children }) => {
    const MyIcon = icon;
    return (
        <div className="flex items-start my-2">
            <MyIcon className="w-4 h-4 event-confirmation-icon mt-1 mr-2 flex-shrink-0" />
            <div className="flex-1">{children}</div>
        </div>
    );
};

const ManageEventButtons = ({ event, settings, token, form }) => {
    const { save: saveCancel, isSaving: isCanceling } = usePublicPostApi(
        `/event/${token}/${event.id}/cancel`,
    );

    function doCancel() {
        if (confirm('Are you sure you want to cancel this?')) {
            saveCancel().then(() => {
                window.location.reload();
            });
        }
    }

    return (
        <>
            {event.limits.canReschedule && !settings?.hideReschedule && (
                <RescheduleButton
                    event={event}
                    token={token}
                    form={form}
                    settings={settings}
                    btnClassName="form-button btn-inverse w-full sm:w-auto"
                />
            )}

            {event.limits.canCancel && !settings?.hideCancel && (
                <button
                    type="button"
                    onClick={() => doCancel()}
                    className="form-button btn-danger w-full sm:w-auto"
                >
                    {isCanceling ? 'Canceling...' : 'Cancel'}
                </button>
            )}
        </>
    );
};

const SingleEventDetails = ({
    event,
    token,
    confirmationUrl,
    form,
    settings,
    isCompactSeriesDetails,
}) => {
    const clientTz = event.client_timezone || getBrowserTimezone();

    const { schedulingSettings } = useContext(ConfirmationContext);

    const isAllDay = event?.event_type?.is_all_day;
    const hideStartTimes = schedulingSettings?.hideStartTimes || isAllDay;
    function formatDisplayTime(timeStr, isEnd) {
        if (hideStartTimes) {
            if (isAllDay && isEnd) {
                return clientFormatDateLong(
                    subDays(new Date(timeStr), 1),
                    clientTz,
                );
            } else {
                return clientFormatDateLong(timeStr, clientTz);
            }
        } else {
            return clientFormatDateTimeLong(timeStr, clientTz);
        }
    }

    function endsOnDifferentDate(event) {
        if (event?.event_type?.is_all_day) {
            // If all day don't include that extra day at the end in case we rollover a little
            //  I'll probably regret this later :-) we should really just be cleaning up the times booked
            //  but for a user saying "1 day" duration and picking just the start date makes sense
            //  even though that's <24hr for 1 day
            return (
                clientFormatDateLong(event.client_start, clientTz) !==
                clientFormatDateLong(
                    subDays(new Date(event.client_end), 1),
                    clientTz,
                )
            );
        } else {
            return (
                clientFormatDateLong(event.client_start, clientTz) !==
                clientFormatDateLong(event.client_end, clientTz)
            );
        }
    }

    return (
        <div className={`flex flex-col mb-6`}>
            <div className="flex items-center">
                <div
                    className={`${isCompactSeriesDetails ? 'flex-1' : 'mx-auto max-w-[375px]'}`}
                >
                    {!isCompactSeriesDetails && (
                        <div className="text-lg font-bold">
                            {event.event_type?.name}
                        </div>
                    )}

                    {!!event.canceled_at && (
                        <div className="text-red-500 font-semibold">
                            This has been canceled
                        </div>
                    )}
                    {endsOnDifferentDate(event) && (
                        <EventDetailsRow icon={CalendarMarkIcon}>
                            {formatDisplayTime(event.client_start)}
                            {!schedulingSettings?.hideDuration
                                ? ` - ${formatDisplayTime(event.client_end, true)}`
                                : ``}
                        </EventDetailsRow>
                    )}
                    {!endsOnDifferentDate(event) && (
                        <>
                            <EventDetailsRow icon={CalendarMarkIcon}>
                                {clientFormatDateLong(
                                    event.client_start,
                                    clientTz,
                                )}
                            </EventDetailsRow>
                            {!hideStartTimes && (
                                <EventDetailsRow icon={CircleClockIcon}>
                                    {clientFormatTime(
                                        event.client_start,
                                        clientTz,
                                    )}
                                    {!schedulingSettings?.hideDuration
                                        ? ` - ${clientFormatTime(event.client_end, clientTz)}`
                                        : ``}
                                </EventDetailsRow>
                            )}
                        </>
                    )}

                    {event.bookable_resource?.type === 'Person' &&
                        !schedulingSettings?.hideBookableResource && (
                            <EventDetailsRow icon={UserIcon}>
                                {event.bookable_resource?.name}
                            </EventDetailsRow>
                        )}

                    {!!event.location && (
                        <EventDetailsRow icon={MapPinIcon}>
                            <Linkify>{event.location}</Linkify>
                        </EventDetailsRow>
                    )}
                </div>
                {isCompactSeriesDetails && (
                    <AddToCalendar
                        event={event}
                        confirmationUrl={confirmationUrl}
                    />
                )}
            </div>

            {!event.canceled_at && (
                <div className={`flex flex-col mt-4 sm:flex-row gap-y-4 gap-x-4 items-stretch sm:items-center ${!isCompactSeriesDetails && (event.limits.canReschedule || event.limits.canCancel) && (!settings?.hideReschedule || !settings?.hideCancel) ? 'sm:justify-between' : 'sm:justify-center'}`}>
                    {!isCompactSeriesDetails && (
                        <AddToCalendar
                            event={event}
                            confirmationUrl={confirmationUrl}
                        />
                    )}

                    {!isCompactSeriesDetails && (
                        <div className="flex flex-col sm:flex-row gap-y-4 gap-x-6 w-full sm:w-auto">
                            <ManageEventButtons
                                event={event}
                                settings={settings}
                                token={token}
                                form={form}
                            />
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const EventDetailsForType = ({
    eventsForType,
    token,
    confirmationUrl,
    form,
    settings,
}) => {
    const clientTz = eventsForType[0].client_timezone;

    return (
        <>
            <div className="flex items-center justify-between mb-6 mt-4">
                <div>
                    <div className="text-xl font-bold">
                        {eventsForType[0].event_type.name}
                    </div>
                    <div>
                        {clientFormatDateLong(
                            eventsForType[0].client_start,
                            clientTz,
                        )}{' '}
                        -{' '}
                        {clientFormatDateLong(
                            eventsForType[eventsForType.length - 1]
                                .client_start,
                            clientTz,
                        )}
                    </div>
                </div>
                <div>
                    <ManageEventButtons
                        event={eventsForType[0]}
                        settings={settings}
                        token={token}
                        form={form}
                    />
                </div>
            </div>
            {eventsForType.map(event => (
                <SingleEventDetails
                    event={event}
                    key={event.id}
                    token={token}
                    settings={settings}
                    confirmationUrl={confirmationUrl}
                    form={form}
                    isCompactSeriesDetails={true}
                />
            ))}
        </>
    );
};

const EventDetails = ({ events, token, confirmationUrl, form, settings }) => {
    const eventsByType = Object.groupBy(events, e => {
        if (e.event_type.is_series) {
            return e.event_type_id;
        } else {
            // Non-series always separate
            return 'ev' + e.id;
        }
    });

    const groupedEvents = Object.values(eventsByType);

    return (
        <div>
            {groupedEvents.map((eventsForType, idx) => (
                <>
                    {eventsForType.length === 1 && (
                        <SingleEventDetails
                            event={eventsForType[0]}
                            key={eventsForType[0].id}
                            token={token}
                            settings={settings}
                            confirmationUrl={confirmationUrl}
                            form={form}
                        />
                    )}
                    {eventsForType.length > 1 && (
                        <EventDetailsForType
                            eventsForType={eventsForType}
                            key={idx}
                            token={token}
                            settings={settings}
                            confirmationUrl={confirmationUrl}
                            form={form}
                        />
                    )}
                </>
            ))}
        </div>
    );
};

export default EventDetails;
